/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useUpdateBookingRoomRates } from '@hooks/accommodation';
import { X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AccommodationBooking } from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';
import { FinancialEditReasonField } from './FinancialEditReasonField';

interface EditRoomRatesModalProps {
  isOpen: boolean;
  booking: AccommodationBooking;
  onClose: () => void;
}

export const EditRoomRatesModal: React.FC<EditRoomRatesModalProps> = ({
  isOpen,
  booking,
  onClose,
}) => {
  const updateRates = useUpdateBookingRoomRates();
  const rooms = useMemo(() => booking.booking_rooms ?? [], [booking]);
  const [rates, setRates] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setRates(
      Object.fromEntries(
        rooms.map((room) => [room.id, String(room.price_per_night)]),
      ),
    );
    setReason('');
  }, [isOpen, rooms]);

  // Mirrors update_booking_room_rates: apply the subtotal difference to the
  // current total, so earlier adjustments to the total are preserved.
  const totalDelta = rooms.reduce((sum, room) => {
    const nextRate = parseFloat(rates[room.id] ?? '');
    if (Number.isNaN(nextRate)) return sum;
    return sum + (nextRate * room.nights - room.subtotal);
  }, 0);
  const newTotal = booking.total_amount + totalDelta;
  const changedRooms = rooms.filter((room) => {
    const nextRate = parseFloat(rates[room.id] ?? '');
    return !Number.isNaN(nextRate) && nextRate !== room.price_per_night;
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const invalid = rooms.some((room) => {
      const nextRate = parseFloat(rates[room.id] ?? '');
      return Number.isNaN(nextRate) || nextRate <= 0;
    });
    if (invalid) {
      apiError('Every room rate must be greater than zero.');
      return;
    }

    if (changedRooms.length === 0) {
      apiError('No rate changes to save.');
      return;
    }

    if (newTotal < (booking.amount_paid ?? 0)) {
      apiError(
        'The new total would be less than the amount already paid. Correct the payments first.',
      );
      return;
    }

    if (!reason.trim()) {
      apiError('Enter a reason for this change.');
      return;
    }

    updateRates.mutate(
      {
        bookingId: booking.id,
        rates: changedRooms.map((room) => ({
          booking_room_id: room.id,
          price_per_night: parseFloat(rates[room.id]),
        })),
        reason: reason.trim(),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Edit room rates
            </h3>
            <p className="text-xs text-gray-400">
              {booking.client
                ? `${booking.client.first_name} ${booking.client.last_name} · `
                : ''}
              Booking #{booking.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div className="max-h-[20rem] space-y-2 overflow-y-auto">
            {rooms.map((room) => {
              const nextRate = parseFloat(rates[room.id] ?? '');
              const isChanged =
                !Number.isNaN(nextRate) && nextRate !== room.price_per_night;
              return (
                <div
                  key={room.id}
                  className={[
                    'grid grid-cols-[1fr_140px] items-center gap-3 rounded-xl border px-3.5 py-3',
                    isChanged
                      ? 'border-amber-200 bg-amber-50/50'
                      : 'border-gray-100 bg-slate-50/50',
                  ].join(' ')}
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Room {room.room?.room_number ?? '—'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {room.room?.room_type?.name ?? ''} · {room.nights} night
                      {room.nights !== 1 ? 's' : ''} · was {booking.currency}{' '}
                      {room.price_per_night.toLocaleString()}/night
                    </p>
                  </div>
                  <div>
                    <label htmlFor={`rate-${room.id}`} className="sr-only">
                      Rate per night for room {room.room?.room_number}
                    </label>
                    <input
                      id={`rate-${room.id}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={rates[room.id] ?? ''}
                      onChange={(e) =>
                        setRates((prev) => ({
                          ...prev,
                          [room.id]: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-right text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5 text-xs">
            <span className="font-medium text-gray-500">Booking total</span>
            <span className="font-semibold text-gray-900">
              {changedRooms.length > 0 ? (
                <>
                  <span className="mr-1.5 font-medium text-gray-400 line-through">
                    {booking.currency} {booking.total_amount.toLocaleString()}
                  </span>
                  <span className="text-pos-primary">
                    {booking.currency} {newTotal.toLocaleString()}
                  </span>
                </>
              ) : (
                `${booking.currency} ${booking.total_amount.toLocaleString()}`
              )}
            </span>
          </div>

          <FinancialEditReasonField
            id="err-reason"
            value={reason}
            onChange={setReason}
          />

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                updateRates.isPending ||
                changedRooms.length === 0 ||
                !reason.trim()
              }
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {updateRates.isPending ? 'Saving…' : 'Save rates'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
